import type { ReactNode } from 'react';
import { fetchKatalogPaket } from '../api/client';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import { ErrorCard, LoadingState } from '../states';

/**
 * Paket & harga — Workforce (BLD-005, 26 Sep 2026).
 *
 * Sama persis pola CRM (`apps/web/src/pages/PaketPage.tsx`): menampilkan harga + cara bayar
 * (transfer bank; QRIS menyusul begitu Owner punya gambarnya) + tombol WhatsApp siap-kirim.
 * Aktivasi TETAP manual oleh tim HBS — halaman ini tidak pernah memanggil endpoint tulis apa pun.
 *
 * Workforce cuma satu tier ("Core", per-karyawan) jadi tidak ada grid pilihan paket seperti CRM.
 * Pesan WA sengaja TIDAK menyebut nama organisasi — `WorkforceRequestContext` hanya membawa
 * `organizationId` (UUID), bukan nama; meminta pelanggan menyebutkan nama organisasinya sendiri
 * di chat lebih jujur daripada mengirim UUID yang tidak berarti apa-apa bagi mereka.
 */
export function PaketPage(): ReactNode {
  const session = useSession();
  const katalog = useAsync(() => fetchKatalogPaket(session.auth), [session.auth]);

  if (katalog.state.status === 'loading') return <LoadingState label="Memuat paket & harga…" />;
  if (katalog.state.status === 'error') {
    return <ErrorCard error={katalog.state.error} onRetry={katalog.reload} />;
  }
  const d = katalog.state.data;

  const pesanWa = encodeURIComponent(
    `Halo, saya ingin konfirmasi pembayaran paket Workforce ${d.paket.nama} untuk organisasi ` +
      `saya [sebutkan nama organisasi Anda]. Saya sudah bayar via QRIS/transfer bank.`,
  );
  const tautanSudahBayar = `${d.kontak.whatsapp}?text=${pesanWa}`;

  return (
    <>
      <section className="card">
        <h2>Paket {d.paket.nama}</h2>
        <p>{d.paket.ringkasHarga}</p>
        <p className="muted small">{d.paket.ringkasTambahan}</p>
        <p className="muted small">{d.paket.catatanHarga}</p>
        <ul>
          {d.paket.layanan.map((l) => (
            <li key={l}>{l}</li>
          ))}
        </ul>
      </section>

      <section className="card">
        <h2>Cara bayar</h2>
        {d.pembayaran.qrisUrl && (
          <div>
            <p className="muted small">QRIS</p>
            <img
              src={d.pembayaran.qrisUrl}
              alt="Kode QRIS SalesHub Pro Workforce"
              width={160}
              height={160}
            />
          </div>
        )}
        <p className="muted small">Transfer bank</p>
        <p>
          {d.pembayaran.bank} · {d.pembayaran.nomorRekening} · a.n. {d.pembayaran.atasNama}
        </p>
      </section>

      <section className="card">
        <h2>Mengaktifkan atau naik paket</h2>
        <p className="muted small">{d.catatan}</p>
        <div className="row">
          <a
            className="button-primary"
            href={d.kontak.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
          >
            WhatsApp {d.kontak.nama}
          </a>
          <a
            className="button-primary"
            href={tautanSudahBayar}
            target="_blank"
            rel="noopener noreferrer"
          >
            Saya sudah bayar
          </a>
        </div>
      </section>
    </>
  );
}
